import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import type { ChannelEnum } from 'js/src/client';
import { ApiService } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { PrimaryButton, SecondaryButton } from 'js/src/components/common/ui/Button';
import Modal from 'js/src/components/common/ui/modal';
import type { TypeInputChangeEvent } from 'js/src/components/common/ui/TextInput';
import TextInput, { TEXT_INPUT_VARIANTS } from 'js/src/components/common/ui/TextInput';
import { hideModal, isSubmitFeedbackModalShowing } from 'js/src/data/modals';
import { FREE_PLAN_NAME } from 'js/src/libs/consts';
import { useAppDispatch } from 'js/src/libs/RootState';

const IS_PAID_PLAN_USER = window.SERVER?.ANALYTICS?.plan_name !== FREE_PLAN_NAME;

interface SubmitFeedbackModalProps {
    showing?: boolean;
    onHide?: VoidFunction;
    channel?: ChannelEnum;
}

export const SubmitFeedbackModal: React.FC<SubmitFeedbackModalProps> = ({
    showing,
    onHide,
    channel,
}) => {
    const reduxShowing = useSelector(isSubmitFeedbackModalShowing);
    const dispatch = useAppDispatch();
    const isShowing = showing ?? reduxShowing;
    const [feedbackText, setFeedbackText] = useState('');
    const onChangeFeedbackText = (e: TypeInputChangeEvent) => setFeedbackText(e.target.value);

    const onClose = useCallback(() => {
        setFeedbackText('');
        if (onHide) {
            onHide();
        } else {
            dispatch(hideModal());
        }
    }, [onHide, dispatch]);

    const onSubmit = useCallback(() => {
        try {
            ApiService.submitFeedbackCreate({
                body: {
                    page: window.location.pathname,
                    feedback: feedbackText,
                    ...(channel && { channel }),
                },
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to submit feedback: ', e);
        }
        Toast.success('Thanks for your feedback!');
        onClose();
    }, [feedbackText, channel, onClose]);

    const calendlyUrl = 'https://calendar.app.google/ePRUahYoRbY2uTGT7';

    return (
        <Modal showing={isShowing} title="Submit feedback" onHidden={onClose}>
            <div>
                <form onSubmit={onSubmit}>
                    <TextInput
                        labelText={<div className="fw-semibold">Add your feedback</div>}
                        lines={5}
                        placeholder="Your feedback..."
                        text={feedbackText}
                        variant={TEXT_INPUT_VARIANTS.TEXTAREA}
                        onChange={onChangeFeedbackText}
                    />
                </form>
                {IS_PAID_PLAN_USER && (
                    <div className="d-flex gap-2 mt-l5-8">
                        <i className="material-symbols-outlined">support_agent</i>
                        <div>
                            Easier to talk to us directly? Book a call with someone from the Lumen5
                            product team{' '}
                            <a
                                className="text-decoration-underline"
                                href={calendlyUrl}
                                target="_blank"
                                rel="noreferrer"
                                onClick={onClose}
                            >
                                here.
                            </a>
                        </div>
                    </div>
                )}
            </div>
            <div className="modal-button-footer">
                <SecondaryButton text="Cancel" onClick={onClose} />
                <PrimaryButton text="Submit" disabled={!feedbackText} onClick={onSubmit} />
            </div>
        </Modal>
    );
};
